import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@eo/shared';

import { formatHours, stopRowEvent } from './helpers';
import { ShareForm } from './ShareForm';
import type { TeamCellProps } from './types';

// Who else holds a share of the client; the CFO keeps whatever is left.
export function TeamCell({ row, people, onPlan }: TeamCellProps) {
  const [open, setOpen] = useState<string | null>(null);

  const save = async (share: { userId: string; fixedHours: number; variableHours: number }) => {
    await onPlan({ kind: 'share', clientId: row.clientId, ...share });
    setOpen(null);
  };

  return (
    <span
      onClick={stopRowEvent}
      onKeyDown={stopRowEvent}
      className="flex max-w-72 min-w-40 flex-wrap items-center gap-x-2 gap-y-1"
    >
      {row.plan.team.map((share) => (
        <span key={share.userId} className="flex items-center gap-1">
          <Popover
            open={open === share.userId}
            onOpenChange={(next) => setOpen(next ? share.userId : null)}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Edit ${share.name} on ${row.name}`}
                className="rounded-8 px-1.5 py-0.5 text-sm tabular-nums text-text-secondary transition-colors hover:bg-foreground/[0.06] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"
              >
                {share.name} {formatHours(share.fixedHours)} / {formatHours(share.variableHours)}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-72"
              onClick={stopRowEvent}
              onKeyDown={stopRowEvent}
            >
              <ShareForm people={people} initial={share} onSave={save} />
            </PopoverContent>
          </Popover>
          <button
            type="button"
            aria-label={`Remove ${share.name} from ${row.name}`}
            onClick={() =>
              void onPlan({ kind: 'unshare', assignmentId: share.assignmentId }).catch(() => {})
            }
            className="rounded-8 p-0.5 text-text-tertiary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"
          >
            <X className="size-3.5" />
          </button>
        </span>
      ))}
      <Popover open={open === 'new'} onOpenChange={(next) => setOpen(next ? 'new' : null)}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`Add someone to ${row.name}`}
            className="flex items-center gap-1 rounded-8 px-1.5 py-0.5 text-xs text-text-tertiary transition-colors hover:bg-foreground/[0.06] hover:text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus"
          >
            <Plus className="size-3.5" />
            Add
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-72"
          onClick={stopRowEvent}
          onKeyDown={stopRowEvent}
        >
          <ShareForm people={people} initial={null} onSave={save} />
        </PopoverContent>
      </Popover>
    </span>
  );
}
